import {
  BlotterTableStorageContext,
  useWLCustomerUserConfigContext,
  type BlotterTableState,
  type BlotterTableStorageContextProps,
} from '@talos/kyoko';
import { useEffect, useMemo, useRef, type PropsWithChildren } from 'react';

const BLOTTER_PREFIX = 'blotters3.';

function getBlotterKey(blotterID: string): `blotters3.${string}` {
  return `${BLOTTER_PREFIX}${blotterID}`;
}

function updateBlotterState(
  updateConfig: ReturnType<typeof useWLCustomerUserConfigContext>['updateConfig'],
  blotterID: string,
  blotterState: BlotterTableState
) {
  return updateConfig({
    [getBlotterKey(blotterID)]: blotterState,
  });
}

function getExistingBlotter(
  config: ReturnType<typeof useWLCustomerUserConfigContext>['config'],
  blotterID: string
): BlotterTableState | undefined {
  return config[getBlotterKey(blotterID)];
}

function clearAllPrefixedBlotterStates(
  updateConfig: ReturnType<typeof useWLCustomerUserConfigContext>['updateConfig'],
  config: ReturnType<typeof useWLCustomerUserConfigContext>['config']
) {
  const blotterKeys = Object.keys(config).filter((key): key is `blotters3.${string}` => key.startsWith(BLOTTER_PREFIX));
  if (blotterKeys.length === 0) {
    return; // Nothing to do, just exit early
  }

  const clearedConfig = Object.fromEntries(blotterKeys.map(key => [key, undefined]));
  return updateConfig(clearedConfig);
}

export function BlotterTableStorageProvider({ children }: PropsWithChildren) {
  const { config, updateConfig, isLoaded } = useWLCustomerUserConfigContext();

  const configRef = useRef(config);
  useEffect(() => {
    configRef.current = config;
  }, [config]);

  const value = useMemo(
    () =>
      isLoaded
        ? {
            getState: blotterID => getExistingBlotter(configRef.current, blotterID),
            setGridState: (blotterID, gridState) => {
              const existingBlotterValue = getExistingBlotter(configRef.current, blotterID);
              const newBlotterState: BlotterTableState = {
                ...existingBlotterValue,
                gridState,
              };

              return updateBlotterState(updateConfig, blotterID, newBlotterState);
            },
            setExtraGridState: (blotterID, extraGridState) => {
              const existingBlotterValue = getExistingBlotter(configRef.current, blotterID);
              const newBlotterState: BlotterTableState = {
                ...existingBlotterValue,
                extraGridState,
              };

              return updateBlotterState(updateConfig, blotterID, newBlotterState);
            },
            cloneState: (fromBlotterID, toBlotterID) => {
              const fromBlotter = getExistingBlotter(configRef.current, fromBlotterID);
              if (fromBlotter == null) {
                return;
              }

              return updateBlotterState(updateConfig, toBlotterID, fromBlotter);
            },
            setFilterState: (blotterID, filter) =>
              updateBlotterState(updateConfig, blotterID, {
                ...getExistingBlotter(configRef.current, blotterID),
                filter,
              }),
            clearAllBlotterState: () => clearAllPrefixedBlotterStates(updateConfig, configRef.current),
          }
        : null,
    [isLoaded, updateConfig]
  ) as BlotterTableStorageContextProps;

  return <BlotterTableStorageContext.Provider value={value}>{children}</BlotterTableStorageContext.Provider>;
}
